import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { PuestoProps } from '../components/Concursos/Gestion/TramitarConcursos/Commons/PuestosTable/PuestoRow';
import { CONCURSOAPI, PONDERACIONAPI } from '../const';
import { isFulfilledAction, isPendingAction, isRejectedAction } from '../utils/redux';

type State = {
  data: null | PuestoProps[];
  dataPublico: null | PuestoProps[];
  requisitos: null | Requisitos[];
  requisitosPublicos: null | Requisitos[];
  error: string;
  success: string;
  loading: boolean;
  file: string;
};

type Requisitos = {
  descripcion: string;
  excluyente: boolean;
  idPuesto: number;
  idRequisitoPuesto: number;
  nombre: string;
  requerido: number;
};

const initialState: State = {
  data: null,
  requisitos: null,
  requisitosPublicos: null,
  error: '',
  loading: false,
  success: '',
  file: '',
  dataPublico: null,
};
const sliceName = 'denominaciones';

type GetPuestosValues = { idLlamadoConcurso: number };
export const getPuestos = createAsyncThunk<PuestoProps[], GetPuestosValues>(
  `${sliceName}/get`,
  async ({ idLlamadoConcurso }) => {
    const response = await axios.get(`${CONCURSOAPI}/puesto/llamadoconcurso/${idLlamadoConcurso}`);
    const data = response.data as any[];

    return data.sort((a, b) => a.idPuesto - b.idPuesto);
  },
);
export const getPuestosPublicos = createAsyncThunk<PuestoProps[], GetPuestosValues>(
  `${sliceName}/getPublico`,
  async ({ idLlamadoConcurso }) => {
    const response = await axios.get(
      `${CONCURSOAPI}/concursos-publicados/puestos/llamado-concurso/${idLlamadoConcurso}`,
    );

    const data = response.data as any[];

    return data.sort((a, b) => a.idPuesto - b.idPuesto);
  },
);

type GetBasesPuesto = { idPuesto: number | undefined };
const getBase = async ({ idPuesto }: GetBasesPuesto) => {
  try {
    const { data } = await axios.get(`${CONCURSOAPI}/puesto/file/bbycc/${idPuesto}`, {
      responseType: 'blob',
    });
    return data;
  } catch (ex) {
    const error = ex as any;
    const { data } = error.response;
    const { message } = data;
    if (message === 'Error. Archivo no encontrado.') {
      return false;
    }
  }
};

export const getBasesPuesto = createAsyncThunk<string, GetBasesPuesto>(
  `${sliceName}/bases/get`,
  async ({ idPuesto }, { dispatch }) => {
    const response = await getBase({ idPuesto });
    if (response === false) {
      const { data } = await axios.post(`${CONCURSOAPI}/puesto/file/bbycc/${idPuesto}`);
      if (data) {
        dispatch(getBasesPuesto({ idPuesto }));
        return '';
      }
    }
    const file = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);
    return url;
  },
);

const getBasePublica = async ({ idPuesto }: GetBasesPuesto) => {
  try {
    const { data } = await axios.get(`${CONCURSOAPI}/concursos-publicados/file/bbycc/${idPuesto}`, {
      responseType: 'blob',
    });
    return data;
  } catch (ex) {
    const error = ex as any;
    const { data } = error.response;
    const { message } = data;
    if (message === 'Error. Archivo no encontrado.') {
      alert(message);
      return false;
    }
    alert('Error al intentar recuperar el archivo de Bases y Condiciones');
    console.log(message);
  }
};
export const getBasesPuestoPublicas = createAsyncThunk<string, GetBasesPuesto>(
  `${sliceName}/bases/getPublicas`,
  async ({ idPuesto }, { dispatch }) => {
    const response = await getBasePublica({ idPuesto });
    if (response === false) {
      return '';
    }
    const file = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);
    return url;
  },
);

type GetRequisitosProps = { idPuesto: number };
export const getRequisitoPublicosPorPuesto = createAsyncThunk<Requisitos[], GetRequisitosProps>(
  `${sliceName}/requisitosPublicos/get`,
  async ({ idPuesto }) => {
    const { data } = await axios.get(
      `${CONCURSOAPI}/concursos-publicados/requisitos/puesto/${idPuesto}`,
    );
    return data;
  },
);

export const getRequisitoPorPuesto = createAsyncThunk<Requisitos[], GetRequisitosProps>(
  `${sliceName}/requisitos/get`,
  async ({ idPuesto }) => {
    const { data } = await axios.get(`${CONCURSOAPI}/requisitopuesto/puesto/${idPuesto}`);
    return data;
  },
);
type GetGrillaPuesto = { idPuesto: number };
const getGrilla = async ({ idPuesto }: GetGrillaPuesto) => {
  try {
    const { data } = await axios.get(
      //`${CONCURSOAPI}/concursos-publicados/file/bbycc/${idPuesto}`,
      `${PONDERACIONAPI}/acta-grilla-pond/${idPuesto}`,
      { responseType: 'blob' },
    );

    return data;
  } catch (ex) {
    const error = ex as any;
    const { data } = error.response;
    const { message } = data;
    console.log('Error al obtener el reporte de grilla de ponderación');
    if (message === 'Error. Archivo no encontrado.') {
      return false;
    }
    throw ex;
  }
};
export const getGrillaPuesto = createAsyncThunk<string, GetGrillaPuesto>(
  `${sliceName}/grillapuesto/get`,
  async ({ idPuesto }, { dispatch }) => {
    const response = await getGrilla({ idPuesto });
    if (response === false) {
      dispatch(getGrillaPuesto({ idPuesto }));

      return '';
    }
    const file = new Blob([response], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(file);

    return url;
  },
);
const denominacionesSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    cleanMsg: (state) => ({ ...state, success: '', error: '' }),
    cleanState: () => initialState,
    cleanRequisitos: (state) => ({ ...state, requisitosPublicos: null }),
    cleanFile: (state) => ({ ...state, file: '', bases: '' }),
  },
  extraReducers: (builder) => {
    builder.addCase(getPuestos.fulfilled, (state, action) => {
      state.data = action.payload;
    });
    builder.addCase(getGrillaPuesto.fulfilled, (state, action) => {
      state.file = action.payload;
    });
    builder.addCase(getPuestosPublicos.fulfilled, (state, action) => {
      state.dataPublico = action.payload;
    });
    builder.addCase(getRequisitoPorPuesto.fulfilled, (state, action) => {
      state.requisitos = action.payload;
    });
    builder.addCase(getRequisitoPublicosPorPuesto.fulfilled, (state, action) => {
      state.requisitosPublicos = action.payload;
    });
    builder.addCase(getBasesPuesto.fulfilled, (state, action) => {
      state.file = action.payload;
    });
    builder.addCase(getBasesPuestoPublicas.fulfilled, (state, action) => {
      state.file = action.payload;
    });
    builder.addCase(getBasesPuestoPublicas.rejected, (state) => {
      state.error = 'Error al obtener el archivo de bases y condiciones';
    });

    builder.addCase(getGrillaPuesto.pending, (state, action) => {
      state.loading = true;
    });

    builder.addMatcher(isRejectedAction(sliceName), (state, action) => {
      state.loading = false;
      state.error = 'No se pudo obtener la información.';
    });

    builder.addMatcher(isPendingAction(sliceName), (state) => {
      state.loading = true;
    });

    builder.addMatcher(isFulfilledAction(sliceName), (state) => {
      state.loading = false;
      state.success = 'Se obtuvo la información con éxito';
    });
  },
});

export const useDenominaciones = () => useSelector((state: any) => state.denominaciones) as State;
export const { cleanMsg, cleanState, cleanRequisitos, cleanFile } = denominacionesSlice.actions;
const denominacionesReducer = denominacionesSlice.reducer;
export default denominacionesReducer;
